import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import type { CheckResult, CheckConfig, PhotoStep, Session, PhotoMetadata } from '@transpordiamet/shared';
import { uploadPhoto } from '@/lib/uploadPhoto';
import { photoLog } from '@/lib/photoTelemetry';
import { getErrorMessage } from '@/lib/errors';
import { buildPiexifExifData } from '@/lib/exifWriter';
import { useCheckConfirmation, type PendingCheckData } from './useCheckConfirmation';

interface UsePhotoUploadOptions {
  sessionId: string;
  stepId: string;
  currentStep: PhotoStep;
  onSuccess: (session: Session) => void;
  onCheckRejected: () => void;
  /** The JPEG is in R2. The caller can drop its local copy. */
  onStored?: () => void;
}

/** uploading: local checks and the R2 PUT. validating: the server checks on /upload/create. */
export type UploadStage = 'idle' | 'uploading' | 'validating';

export interface UsePhotoUploadReturn {
  // Upload state
  stage: UploadStage;
  /** The photo in R2, once the PUT is done. Null before that or without R2_PUBLIC_DOMAIN. */
  r2PhotoUrl: string | null;
  error: string | null;

  // Check confirmation state
  showCheckConfirmation: boolean;
  pendingCheckData: PendingCheckData | null;
  confirming: boolean;

  // Handlers
  startUpload: (photoBlob: Blob, metadata: PhotoMetadata) => Promise<void>;
  handleConfirmCheck: () => Promise<void>;
  handleRejectCheck: () => void;
  presentDraft: (checkResult: CheckResult, photoId: string) => void;
  clearError: () => void;
}

export { type PendingCheckData };

/**
 * Hook to manage photo upload workflow including check confirmation
 */
export function usePhotoUpload(options: UsePhotoUploadOptions): UsePhotoUploadReturn {
  const { sessionId, stepId, currentStep, onSuccess, onCheckRejected, onStored } = options;
  const navigate = useNavigate();

  // Upload state
  const [stage, setStage] = useState<UploadStage>('idle');
  const [r2PhotoUrl, setR2PhotoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check confirmation workflow (extracted to separate hook)
  const checkConfirmation = useCheckConfirmation({
    onSuccess,
    onCheckRejected,
  });

  /** Runs as soon as the camera returns a photo; there is no confirm step. */
  const startUpload = useCallback(
    async (photoBlob: Blob, metadata: PhotoMetadata) => {
      setError(null);
      setR2PhotoUrl(null);

      try {
        // Get checks to run from current step (default to empty array if not defined)
        const checksToRun: CheckConfig[] = currentStep.checks || [];

        // Build EXIF data from metadata - ensures consistency between blob and API
        const exifData = buildPiexifExifData(metadata);

        // Start upload with checks
        setStage('uploading');
        photoLog('info', 'upload started', { stepId });

        const result = await uploadPhoto({
          sessionId,
          stepId,
          photoBlob,
          checksToRun,
          exifData,
          metadata,
          onUploaded: (publicUrl) => {
            setR2PhotoUrl(publicUrl ?? null);
            setStage('validating');
            photoLog('info', 'validating', { stepId });
            if (publicUrl) onStored?.();
          },
        });

        if (result.success && result.requiresCheckConfirmation) {
          // Check completed, awaiting user confirmation (VIN or bypass)
          // Draft photo already created, just need to confirm it
          setStage('idle');
          checkConfirmation.setPendingCheckData({
            checkResult: result.checkResult!,
            sessionId,
            photoId: result.draftPhoto!.id,
          });
          checkConfirmation.setShowCheckConfirmation(true);
          photoLog('info', 'dialog shown', { stepId, check: result.checkResult?.type ?? '' });
        } else if (result.success && result.session) {
          // Upload completed successfully - photo was auto-confirmed
          // (no VIN confirmation or bypass required)
          setStage('idle');

          // Notify parent component of success
          onSuccess(result.session);
        } else {
          // Upload failed (either checks failed or upload error)

          // Check if this is a timeout error - navigate to restart page
          if (result.errorCode === 'PHOTO_UPLOAD_TIMEOUT') {
            console.log('[PhotoUpload] Timeout detected, navigating to restart page');
            setStage('idle');
            navigate(`/${sessionId}/restart`);
            return;
          }

          // For other errors, display the error message
          setError(result.error || t`Photo upload failed`);
          setStage('idle');
        }
      } catch (error) {
        console.error('[PhotoUpload] Upload error:', error);
        setError(getErrorMessage(error));
        setStage('idle');
      }
    },
    [sessionId, stepId, currentStep, onSuccess, onStored, checkConfirmation, navigate]
  );

  // A new step must not keep the previous photo's URL, or the next shutter would drop its JPEG.
  useEffect(() => {
    setR2PhotoUrl((url) => (url === null ? url : null));
  }, [stepId]);

  const presentDraft = useCallback(
    (checkResult: CheckResult, photoId: string) => {
      checkConfirmation.setPendingCheckData({ checkResult, sessionId, photoId });
      checkConfirmation.setShowCheckConfirmation(true);
      photoLog('info', 'dialog shown', { stepId, check: checkResult.type });
    },
    [checkConfirmation, sessionId, stepId]
  );

  /** Drops the error and the R2 URL, back to idle. */
  const clearError = useCallback(() => {
    setError(null);
    setR2PhotoUrl(null);
    setStage('idle');
  }, []);

  return {
    // Upload state
    stage,
    r2PhotoUrl,
    error,

    // Check confirmation state (from useCheckConfirmation hook)
    showCheckConfirmation: checkConfirmation.showCheckConfirmation,
    pendingCheckData: checkConfirmation.pendingCheckData,
    confirming: checkConfirmation.confirming,

    // Handlers
    startUpload,
    handleConfirmCheck: checkConfirmation.handleConfirmCheck,
    handleRejectCheck: checkConfirmation.handleRejectCheck,
    presentDraft,
    clearError,
  };
}
